import { prismicClient } from '@/data';
import { usePrismicStaleTime } from '@/data/prismicHooks';
import { useActiveProductConfig } from '@/hooks/use-active-product';
import { PrismicTypeWithData } from '@/types/prismic';
import { PromotionThemeDocument, Rafv2Document } from '@/types/prismic.generated';
import { QueryFunctionContext, useQuery } from '@tanstack/react-query';

type Props = ['rafv2', string | undefined];

export type RafWithTheme = PrismicTypeWithData<
    Rafv2Document,
    {
        theme: PromotionThemeDocument;
    }
>;

const fetcher = async (context: QueryFunctionContext<Props>) => {
    const [key, rafPromotionUid] = context.queryKey;
    return (await prismicClient.getByUID(key, rafPromotionUid ?? '', {
        graphQuery: `{
            rafv2 {
                ...rafv2Fields
                theme {
                    ...themeFields
                }
            }
        }`,
    })) as RafWithTheme;
};

/**
 * Hook to fetch the RAF content from Prismic
 */
export const useRaf = () => {
    const productConfig = useActiveProductConfig();
    const staleTime = usePrismicStaleTime();
    const rafEnabled = !!productConfig?.rafV2PromotionUid;

    const { data, isPending, refetch } = useQuery({
        queryKey: ['rafv2', productConfig?.rafV2PromotionUid],
        queryFn: fetcher,
        staleTime,
        enabled: rafEnabled,
    });

    return {
        data,
        // return pending as true only if we have raf enabled
        isPending: rafEnabled && isPending,
        refetch,
        isRafEnabled: rafEnabled,
    };
};
